import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import { PlusCircle, Building, LayoutGrid, Kanban } from "lucide-react";
import { ControleProjetoCard } from "@/components/controle-projetos/ControleProjetoCard";
import { ControleProjetoForm } from "@/components/controle-projetos/ControleProjetoForm";
import { ProjetoKanban } from "@/components/controle-projetos/ProjetoKanban";
import { useProjectActions } from "@/components/controle-projetos/useProjectActions";
import {
  FiltrosControleProjetos,
  type FiltrosProjetos,
} from "@/components/controle-projetos/FiltrosControleProjetos";
import { RascunhosDialog, type ProjetoRascunho } from "@/components/controle-projetos/RascunhosDialog";
import { FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { writeExpectingRows } from "@/lib/supabase-write";
import { createLocalDate, formatLocalDate } from "@/utils/dateUtils";
import type { ControleProjeto } from "@/types/controle-projeto";
import { calcularEstadoProjeto, isProjetoHistorico } from "@/utils/controle-projeto-estado";
import { anexarPlanejados, fetchPlanejados } from "@/utils/controle-projeto-planejados";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { usePropostasPorProjeto } from "@/hooks/use-finance-propostas";
import { projectArea } from "@/utils/project-area";
import { reportUserError, userErrorToast } from "@/lib/report-error";

// Referências estáveis pro default das queries: um novo [] a cada render
// invalidaria os useMemo que dependem das listas.
const EMPTY_PROJETOS: ControleProjeto[] = [];
const EMPTY_RASCUNHOS: ProjetoRascunho[] = [];

const FILTROS_VAZIOS: FiltrosProjetos = {
  freelancer: "",
  area: "",
  agencia: "",
  tipoContrato: "",
};

// queryFns no nível do módulo (só dependem do client supabase). Em erro,
// deixam propagar pro handler global do TanStack Query (padrão da casa).

// Em curso: ativos (incluindo vencidos, que são fila de decisão) + renovados e
// finalizados ("não renova") cuja conclusão ainda não chegou. Sempre carregados. Não trazem job_evaluations porque só
// projetos que já terminaram exibem avaliações no card.
async function fetchProjetosEmCurso(): Promise<ControleProjeto[]> {
  const hoje = formatLocalDate(new Date());
  const { data, error } = await supabase
    .from("controle_projetos")
    .select(`
      *,
      agencia:agencias(nome, email),
      freelancer:freelancers(nome, email)
    `)
    .is("deleted_at", null)
    .or(
      `status.eq.ativo,and(status.eq.renovado,or(data_conclusao.is.null,data_conclusao.gte.${hoje})),and(status.eq.encerrado,data_conclusao.gte.${hoje})`,
    )
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data || []) as ControleProjeto[];
}

// Histórico: encerrados e renovados já vencidos (~2k linhas) + join de avaliações.
// Carregados só sob demanda, quando o usuário liga "Mostrar histórico" (via `enabled`
// no useQuery), pra não pesar o carregamento padrão da tela.
async function fetchProjetosHistorico(): Promise<ControleProjeto[]> {
  const hoje = formatLocalDate(new Date());
  const { data, error } = await supabase
    .from("controle_projetos")
    .select(`
      *,
      agencia:agencias(nome, email),
      freelancer:freelancers(nome, email),
      job_evaluations(id, evaluator_type, status, rating, media_geral, feedback_text, feedback_data, completed_at, deleted_at)
    `)
    .is("deleted_at", null)
    .or(
      `and(status.eq.encerrado,or(data_conclusao.is.null,data_conclusao.lt.${hoje})),and(status.eq.renovado,data_conclusao.lt.${hoje})`,
    )
    .order("created_at", { ascending: false });

  if (error) throw error;

  return (data || []).map((projeto) => {
    const evaluations = (projeto.job_evaluations || []).filter((e) => !e.deleted_at);
    const freelancerEvaluation = evaluations.find((evaluation) => evaluation.evaluator_type === 'freelancer');
    const agencyEvaluation = evaluations.find((evaluation) => evaluation.evaluator_type === 'agency');

    return {
      ...projeto,
      freelancer_evaluation: freelancerEvaluation,
      agency_evaluation: agencyEvaluation,
    };
  }) as ControleProjeto[];
}

// Nº de cards renderizados por grupo antes do "Mostrar mais". Só entra em ação
// quando o histórico está ligado (~2k cards); sem ele são ~320 e renderizam de uma vez.
const CARDS_POR_PAGINA = 24;

interface AcoesProjeto {
  onRenovar: (projeto: ControleProjeto) => void;
  onEditar: (projeto: ControleProjeto) => void;
  onExcluir: (projetoId: string) => void;
  onFinalizar: (projeto: ControleProjeto) => void;
  onConfirmarPlanejado: (projeto: ControleProjeto) => void;
  onDesfazerFinalizacao: (projeto: ControleProjeto) => void;
}

// Grid de cards de uma agência com renderização progressiva. `paginado` liga o
// limite + botão "Mostrar mais" (dados já estão em memória, é só fatiar).
function ProjetosGridAgencia({
  projetos,
  paginado,
  ...acoes
}: AcoesProjeto & {
  projetos: ControleProjeto[];
  paginado: boolean;
}) {
  const [visiveis, setVisiveis] = useState(CARDS_POR_PAGINA);
  const exibidos = paginado ? projetos.slice(0, visiveis) : projetos;
  const restantes = projetos.length - exibidos.length;

  // Uma única busca em lote de `por-projeto` para os cards visíveis, não uma
  // por card — só dispara pra quem tem a tela `financeiro`.
  const { screens } = useUserPermissions();
  const temFinanceiro = screens.includes("financeiro");
  const propostasPorProjetoQuery = usePropostasPorProjeto(
    exibidos.map((projeto) => projeto.id),
    temFinanceiro,
  );

  return (
    <>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {exibidos.map((projeto) => {
          const proposta = propostasPorProjetoQuery.data
            ? (propostasPorProjetoQuery.data.items[projeto.id] ?? null)
            : undefined;
          return (
            <ControleProjetoCard key={projeto.id} projeto={projeto} proposta={proposta} {...acoes} />
          );
        })}
      </div>
      {restantes > 0 && (
        <div className="flex justify-center pt-1">
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => setVisiveis((atual) => atual + CARDS_POR_PAGINA)}
          >
            Mostrar mais ({restantes})
          </Button>
        </div>
      )}
    </>
  );
}

async function fetchProjeto(id: string): Promise<ControleProjeto | null> {
  const { data, error } = await supabase
    .from("controle_projetos")
    .select(`
      *,
      agencia:agencias(nome, email),
      freelancer:freelancers(nome, email)
    `)
    .eq("id", id)
    .is("deleted_at", null)
    .maybeSingle();

  if (error) throw error;
  return data as ControleProjeto | null;
}

async function fetchRascunhosList(): Promise<ProjetoRascunho[]> {
  const { data, error } = await supabase
    .from("controle_projetos")
    .select(`
      id, agencia_id, freelancer_id, area_atuacao, area_canonica, valor_projeto,
      vaga_id, data_inicio, data_conclusao, nucleo, created_at,
      agencia:agencias(nome),
      freelancer:freelancers(nome)
    `)
    .is("deleted_at", null)
    .eq("status", "rascunho")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data || []) as unknown as ProjetoRascunho[];
}

export default function ControleProjetos() {
  const queryClient = useQueryClient();
  const [mostrarHistorico, setMostrarHistorico] = useState(false);

  // Três recortes DISTINTOS via TanStack Query (cache entre navegações + invalidação
  // centralizada). Em curso e rascunhos sempre carregam (o contador de rascunhos
  // aparece no banner). Histórico só carrega sob demanda, quando o toggle liga.
  const { data: emCurso = EMPTY_PROJETOS } = useQuery({
    queryKey: ["controle-projetos", "em-curso"],
    staleTime: 60_000,
    queryFn: fetchProjetosEmCurso,
  });
  const { data: historico = EMPTY_PROJETOS } = useQuery({
    queryKey: ["controle-projetos", "historico"],
    staleTime: 60_000,
    queryFn: fetchProjetosHistorico,
    enabled: mostrarHistorico,
  });
  const { data: rascunhos = EMPTY_RASCUNHOS } = useQuery({
    queryKey: ["controle-projetos", "rascunho"],
    staleTime: 60_000,
    queryFn: fetchRascunhosList,
  });
  const { data: planejados = EMPTY_PROJETOS } = useQuery({
    queryKey: ["controle-projetos", "planejado"],
    staleTime: 60_000,
    queryFn: fetchPlanejados,
  });
  const projetos = useMemo(
    () => anexarPlanejados(mostrarHistorico ? [...emCurso, ...historico] : emCurso, planejados),
    [emCurso, historico, mostrarHistorico, planejados],
  );
  const [filtros, setFiltros] = useState<FiltrosProjetos>(FILTROS_VAZIOS);
  const [isRascunhosDialogOpen, setIsRascunhosDialogOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [projetoSelecionado, setProjetoSelecionado] = useState<ControleProjeto | null>(null);
  const { actions: projectActions, dialogs: projectActionDialogs } = useProjectActions();
  const [viewMode, setViewMode] = useState<"cards" | "kanban">("cards");
  const [ordenarPorVencimento, setOrdenarPorVencimento] = useState(false);
  const { toast } = useToast();

  // /controle-projetos/novo e /controle-projetos/:id/editar da versão nova chegam aqui
  // com ?novo=1 ou ?editar=<id>: abrem o mesmo formulário dos botões da tela.
  const [searchParams, setSearchParams] = useSearchParams();
  const editarId = searchParams.get("editar");
  const novo = searchParams.get("novo") === "1";
  useEffect(() => {
    if (!editarId && !novo) return;
    const limpar = () =>
      setSearchParams((atual) => {
        atual.delete("editar");
        atual.delete("novo");
        return atual;
      }, { replace: true });
    if (novo) {
      setProjetoSelecionado(null);
      setIsDialogOpen(true);
      limpar();
      return;
    }
    let cancelado = false;
    fetchProjeto(editarId!)
      .then((projeto) => {
        if (cancelado || !projeto) return;
        setProjetoSelecionado(projeto);
        setIsDialogOpen(true);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelado) limpar();
      });
    return () => {
      cancelado = true;
    };
  }, [editarId, novo, setSearchParams]);

  const areas = useMemo(
    () => [...new Set(projetos.map((projeto) => projectArea(projeto)))].filter(Boolean),
    [projetos],
  );

  // Lista filtrada é DERIVADA de projetos + filtros. Guardar o resultado em estado
  // fazia o recarregamento dos dados (toggle de histórico, invalidação após
  // salvar/excluir) sobrescrever a lista com a versão sem filtro, enquanto os
  // selects seguiam mostrando os filtros escolhidos.
  const projetosFiltrados = useMemo(() => {
    return projetos.filter((projeto) => {
      if (filtros.freelancer && projeto.freelancer_id !== filtros.freelancer) return false;
      if (filtros.area && projectArea(projeto) !== filtros.area) return false;
      if (filtros.agencia && projeto.agencia_id !== filtros.agencia) return false;
      if (filtros.tipoContrato === "fixo" && projeto.modalidade !== "fixa") return false;
      if (filtros.tipoContrato === "temporario" && projeto.modalidade === "fixa") return false;
      return true;
    });
  }, [projetos, filtros]);

  const handlePromoverRascunho = async (projetoData: Record<string, unknown>, rascunhoId: string) => {
    try {
      await writeExpectingRows(
        supabase
          .from("controle_projetos")
          .update({ ...projetoData, status: 'ativo' })
          .eq("id", rascunhoId),
        "ativar projeto",
      );
      toast({
        title: "Projeto ativado",
        description: "Rascunho promovido para projeto ativo.",
      });
      // Promover muda status rascunho -> ativo: invalida AMBAS as listas.
      await queryClient.invalidateQueries({ queryKey: ["controle-projetos"] });
    } catch (error) {
      const err = error as Error;
      console.error("Erro ao promover rascunho:", err);
      toast({
        title: "Erro",
        description: "Não foi possível ativar o projeto. " + reportUserError(err, { source: "controle-projetos" }).description,
        variant: "destructive",
      });
    }
  };

  const handleEditarRascunho = async (projetoData: TablesUpdate<"controle_projetos">, rascunhoId: string) => {
    try {
      // Edição sem promover: não enviamos status, então continua como rascunho.
      await writeExpectingRows(
        supabase.from("controle_projetos").update(projetoData).eq("id", rascunhoId),
        "salvar rascunho",
      );
      toast({
        title: "Rascunho atualizado",
        description: "Alterações salvas. O projeto continua em rascunho.",
      });
      await queryClient.invalidateQueries({ queryKey: ["controle-projetos", "rascunho"] });
    } catch (error) {
      const err = error as Error;
      console.error("Erro ao editar rascunho:", err);
      toast({
        title: "Erro",
        description: "Não foi possível salvar o rascunho. " + reportUserError(err, { source: "controle-projetos" }).description,
        variant: "destructive",
      });
    }
  };

  const handleSave = async (projetoData: TablesInsert<"controle_projetos">) => {
    try {
      if (projetoSelecionado) {
        // Estender a data de conclusão de um finalizado reabre o projeto: trigger
        // no banco (sql/187) devolve status 'ativo'; aqui só invalidamos.
        await writeExpectingRows(
          supabase.from("controle_projetos").update(projetoData).eq("id", projetoSelecionado.id),
          "atualizar projeto",
        );

        toast({
          title: "Sucesso",
          description: "Projeto atualizado com sucesso!",
        });
      } else {
        await writeExpectingRows(
          supabase.from("controle_projetos").insert([projetoData]),
          "criar projeto",
        );

        toast({
          title: "Sucesso",
          description: "Projeto criado com sucesso!",
        });
      }

      queryClient.invalidateQueries({ queryKey: ["controle-projetos"] });
      setIsDialogOpen(false);
      setProjetoSelecionado(null);
    } catch (error) {
      toast(userErrorToast(error, { action: "salvar o projeto", source: "controle-projetos" }));
    }
  };

  const handleEditar = (projeto: ControleProjeto) => {
    setProjetoSelecionado(projeto);
    setIsDialogOpen(true);
  };

  const acoes = {
    ...projectActions,
    onEditar: handleEditar,
  };

  const ordenarProjetos = (projetos: ControleProjeto[]) => {
    if (!ordenarPorVencimento) {
      return [...projetos].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }

    const hoje = new Date();
    // Vencidos primeiro (esperam decisão), depois quem vence em até 10 dias, os demais
    // em andamento, fixos e por último quem já terminou.
    const calcularPrioridade = (projeto: ControleProjeto) => {
      const { estado, diasRestantes } = calcularEstadoProjeto(projeto, hoje);
      if (estado === "vencido") return 0;
      if (estado === "finalizado" || estado === "renovado") return 4;
      if (diasRestantes === null) return 3;
      if (diasRestantes <= 10) return 1;
      return 2;
    };

    return [...projetos].sort((a, b) => {
      const prioridadeA = calcularPrioridade(a);
      const prioridadeB = calcularPrioridade(b);
      if (prioridadeA !== prioridadeB) {
        return prioridadeA - prioridadeB;
      }
      if (a.data_conclusao && b.data_conclusao) {
        return createLocalDate(a.data_conclusao).getTime() - createLocalDate(b.data_conclusao).getTime();
      }
      return 0;
    });
  };

  // Com o histórico desligado, quem já terminou (finalizado ou renovado vencido) some.
  // Vencidos sem decisão aparecem sempre: são a fila de Finalizar/Renovar.
  const projetosParaExibir = projetosFiltrados.filter((projeto) => {
    if (mostrarHistorico) return true;
    return !isProjetoHistorico(calcularEstadoProjeto(projeto));
  });

  // "Não renova" fica na coluna Em Andamento: o freelancer ainda trabalha até a data.
  const projetosPorEstado = projetosParaExibir.reduce((acc: Record<string, ControleProjeto[]>, projeto) => {
    const { estado } = calcularEstadoProjeto(projeto);
    const coluna = estado === "nao_renova" ? "em_andamento" : estado;
    if (!acc[coluna]) {
      acc[coluna] = [];
    }
    acc[coluna].push(projeto);
    return acc;
  }, {});

  const projetosPorAgencia = projetosParaExibir.reduce((acc: Record<string, ControleProjeto[]>, projeto) => {
    const nomeAgencia = projeto.agencia?.nome || 'Agência não encontrada';
    if (!acc[nomeAgencia]) {
      acc[nomeAgencia] = [];
    }
    acc[nomeAgencia].push(projeto);
    return acc;
  }, {});

  Object.keys(projetosPorAgencia).forEach(agencia => {
    projetosPorAgencia[agencia] = ordenarProjetos(projetosPorAgencia[agencia]);
  });

  return (
    <div className="space-y-4 fade-in">
      <PageHeader
        title="Controle de Projetos"
        subtitle="Gerencie o andamento dos projetos por agência"
        actions={
          <>
            <div className="flex items-center gap-2">
              <Switch
                id="mostrar-historico"
                checked={mostrarHistorico}
                onCheckedChange={setMostrarHistorico}
              />
              <Label htmlFor="mostrar-historico" className="text-sm">
                Mostrar histórico
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="ordenar-por-vencimento"
                checked={ordenarPorVencimento}
                onCheckedChange={setOrdenarPorVencimento}
              />
              <Label htmlFor="ordenar-por-vencimento" className="text-sm">
                Ordenar por vencimento
              </Label>
            </div>

            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(value) => value && setViewMode(value as "cards" | "kanban")}
            >
              <ToggleGroupItem value="cards" aria-label="Visualização em Cards" className="text-xs">
                <LayoutGrid className="h-3 w-3" />
                Cards
              </ToggleGroupItem>
              <ToggleGroupItem value="kanban" aria-label="Visualização Kanban" className="text-xs">
                <Kanban className="h-3 w-3" />
                Kanban
              </ToggleGroupItem>
            </ToggleGroup>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2 text-sm" size="sm" onClick={() => setProjetoSelecionado(null)}>
                  <PlusCircle className="h-3 w-3" />
                  Novo Projeto
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-lg">
                    {projetoSelecionado ? "Editar Projeto" : "Criar Novo Projeto"}
                  </DialogTitle>
                </DialogHeader>
                <ControleProjetoForm
                  projeto={projetoSelecionado}
                  onSave={handleSave}
                  onCancel={() => {
                    setIsDialogOpen(false);
                    setProjetoSelecionado(null);
                  }}
                />
              </DialogContent>
            </Dialog>
          </>
        }
      />

      {rascunhos.length > 0 && (
        <button
          type="button"
          onClick={() => setIsRascunhosDialogOpen(true)}
          className="w-full flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100"
        >
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                Você tem {rascunhos.length}{" "}
                {rascunhos.length === 1 ? "projeto em rascunho" : "projetos em rascunho"}
              </p>
              <p className="text-xs text-amber-800">
                Projetos criados ao aprovar vagas. Complete os dados para ativá-los.
              </p>
            </div>
          </div>
          <span className="text-sm font-medium text-amber-700">Completar agora →</span>
        </button>
      )}

      <FiltrosControleProjetos
        onFiltrosChange={setFiltros}
        areas={areas}
      />

      {projetosParaExibir.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground">Nenhum projeto encontrado.</p>
        </div>
      ) : viewMode === "kanban" ? (
        <div className="h-[calc(100vh-10rem)]">
          <ProjetoKanban
            projetosPorEstado={projetosPorEstado}
            mostrarHistorico={mostrarHistorico}
            {...acoes}
          />
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(projetosPorAgencia).map(([nomeAgencia, projetosAgencia]) => (
            <div key={nomeAgencia} className="space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b">
                <Building className="h-4 w-4 text-primary" />
                <h2 className="text-lg font-semibold">{nomeAgencia}</h2>
                <span className="text-xs text-muted-foreground">
                  ({projetosAgencia.length} projeto{projetosAgencia.length !== 1 ? 's' : ''})
                </span>
              </div>

              <ProjetosGridAgencia
                projetos={projetosAgencia}
                paginado={mostrarHistorico}
                {...acoes}
              />
            </div>
          ))}
        </div>
      )}

      {projectActionDialogs}

      <RascunhosDialog
        open={isRascunhosDialogOpen}
        onOpenChange={setIsRascunhosDialogOpen}
        rascunhos={rascunhos}
        onPromote={handlePromoverRascunho}
        onSaveDraft={handleEditarRascunho}
      />
    </div>
  );
}
